import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import {
  LayoutDashboard, Repeat, Users, PackageCheck, Boxes, BookOpen, CreditCard,
  Network, Settings, ScrollText, LogOut, Menu, X, Waves,
} from "lucide-react";

const NAV = {
  super_admin: [
    { section: "Panoramica" },
    { to: "/", label: "Dashboard Network", icon: LayoutDashboard },
    { section: "Network" },
    { to: "/network", label: "Partner & Cambio Point", icon: Network },
    { to: "/clienti", label: "Clienti", icon: Users },
    { section: "Operazioni" },
    { to: "/cambi", label: "Cambi", icon: Repeat },
    { to: "/restituzioni", label: "Restituzioni", icon: PackageCheck },
    { section: "Biancheria & Catalogo" },
    { to: "/biancheria", label: "Pool & Movimenti", icon: Boxes },
    { to: "/catalogo", label: "Catalogo", icon: BookOpen },
    { section: "Finanza & Report" },
    { to: "/pagamenti", label: "Pagamenti", icon: CreditCard },
    { to: "/audit", label: "Audit Log", icon: ScrollText },
  ],
  partner_admin: [
    { section: "Panoramica" },
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { section: "Cambi" },
    { to: "/cambio", label: "Nuovo Cambio", icon: Repeat, big: true },
    { to: "/cambi", label: "Storico Cambi", icon: ScrollText },
    { section: "Clienti" },
    { to: "/clienti", label: "Clienti & Strutture", icon: Users },
    { to: "/restituzioni", label: "Restituzioni", icon: PackageCheck },
    { section: "Biancheria" },
    { to: "/biancheria", label: "Pool & Lavorazione", icon: Boxes },
    { to: "/catalogo", label: "Catalogo", icon: BookOpen },
    { section: "Amministrazione" },
    { to: "/pagamenti", label: "Pagamenti", icon: CreditCard },
    { to: "/operatori", label: "Operatori & Config", icon: Settings },
    { to: "/audit", label: "Storico", icon: ScrollText },
  ],
  operator: [
    { to: "/cambio", label: "Nuovo Cambio", icon: Repeat, big: true },
    { to: "/clienti", label: "Clienti", icon: Users },
    { to: "/restituzioni", label: "Restituzioni", icon: PackageCheck },
    { to: "/cambi", label: "Storico Cambi", icon: ScrollText },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = NAV[user.role] || [];

  const initials = (user.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F4F8FC]">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky lg:top-0 z-40 h-screen w-72 shrink-0 bg-[#0B2447] text-white flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-6 py-5 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-[#0EA5C4] flex items-center justify-center"><Waves size={22} /></div>
          <div className="leading-tight">
            <p className="font-display font-extrabold text-lg">CambioBiancheria</p>
            <p className="text-[10px] text-[#0EA5C4] tracking-wide">Porta lo sporco. Ritira il pulito.</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto cb-scroll px-3 py-4 space-y-1">
          {nav.map((item, i) =>
            item.section ? (
              <p key={i} className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/40">{item.section}</p>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                end={item.to === "/"}
                data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `nav-link flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${
                    isActive ? "bg-[#0EA5C4] text-white shadow-lg shadow-cyan-900/30" : "text-white/70 hover:bg-white/10 hover:text-white"
                  } ${item.big ? "font-bold" : ""}`
                }
              >
                <item.icon size={18} /> {item.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#0EA5C4] flex items-center justify-center text-sm font-bold">{initials}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user.name}</p>
              <p className="text-[10px] text-white/50">{ROLE_LABELS[user.role]}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg hover:bg-white/10 text-white/70"><LogOut size={18} /></button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b flex items-center justify-between px-4 py-3">
          <button onClick={() => setOpen(true)} data-testid="menu-toggle" className="p-2"><Menu size={22} /></button>
          <span className="font-display font-bold text-[#0B2447]">CambioBiancheria</span>
          <span className="w-8" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto cb-scroll">
          {children}
        </main>
      </div>
    </div>
  );
}
